// Dokumentenliste: Tabs "Offen" / "Erledigt", Suche, Status-Badges, Pagination, Klick -> Detail.
// Muster: modules/einvoicing/components/InvoiceList.js (Row->Card-Mobilfallback, Filterzeile).
// Tab-Filter + Pagination laufen SERVERSEITIG: das Backend akzeptiert status als
// Komma-Liste (Offen = processing,needs_review,no_ruleset,failed / Erledigt = approved,rejected),
// total/totalPages kommen aus der Backend-Response.
//
// UI-PARITAET (Welle 3, Agent FE-1): zusaetzliche Filter Lieferant/Zeitraum/Uebertragen +
// Sub-Status-Chips (Offen-Tab) + gemeldet-Badge. "Ohne Lieferant" laeuft SERVERSEITIG
// (Backend-Kontrakt supplier='' -> supplier_id IS NULL/''); api.js sendet den Leerstring
// fuer 'supplier' als dokumentierten Sonderfall explizit mit.

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Search, RefreshCw, FileScan, AlertTriangle, Flag } from 'lucide-react';
import { Card, StatusBadge, inputCls, btnGhost, fmtDate, Pager } from './ui';
import { listDocuments, listDocumentSuppliers } from '../api';

const OPEN_STATES = ['processing', 'needs_review', 'no_ruleset', 'failed'];
const DONE_STATES = ['approved', 'rejected'];
const PAGE_SIZE = 20;
const NONE_SUPPLIER = '__none__'; // Sentinel fuer "Ohne Lieferant" (s. Kopfkommentar)

/**
 * Abgleich-Ampel je Zeile (Arbeitsvorrat: Abweichungen auf einen Blick).
 * compare = { ok, diff, notFound } | null (noch kein Abgleich berechnet).
 */
const CompareChips = ({ compare, isDarkMode, t }) => {
  if (!compare) return <span className={isDarkMode ? 'text-white/30' : 'text-slate-300'}>—</span>;
  const chip = (n, tone, label) => n > 0 && (
    <span title={label}
      className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md text-[11px] font-semibold tabular-nums ${tone}`}>
      {n}
    </span>
  );
  const all = (compare.ok || 0) + (compare.diff || 0) + (compare.notFound || 0);
  if (all === 0) return <span className={isDarkMode ? 'text-white/30' : 'text-slate-300'}>—</span>;
  return (
    <span className="inline-flex items-center gap-1">
      {chip(compare.ok, isDarkMode ? 'bg-emerald-500/15 text-emerald-300' : 'bg-emerald-50 text-emerald-700', t('ab.compareOk', { n: compare.ok }))}
      {chip(compare.diff, isDarkMode ? 'bg-amber-500/15 text-amber-300' : 'bg-amber-50 text-amber-700', t('ab.compareDiff', { n: compare.diff }))}
      {chip(compare.notFound, isDarkMode ? 'bg-red-500/15 text-red-300' : 'bg-red-50 text-red-600', t('ab.compareNotFound', { n: compare.notFound }))}
    </span>
  );
};

const ReportedFlag = ({ isDarkMode, t }) => (
  <span title={t('ab.shell.reportedTooltip')} className={`inline-flex items-center gap-1 ${isDarkMode ? 'text-red-300' : 'text-red-600'}`}>
    <Flag className="w-3.5 h-3.5" />
  </span>
);

const MobileDocRow = ({ r, isDarkMode, t, onOpen }) => {
  const textMain = isDarkMode ? 'text-white/85' : 'text-slate-700';
  const textMuted = isDarkMode ? 'text-white/55' : 'text-slate-500';
  const meta = (label, value) => (
    <div className="min-w-0">
      <div className={`text-[11px] uppercase tracking-wide ${textMuted}`}>{label}</div>
      <div className={`text-xs truncate ${textMain}`}>{value}</div>
    </div>
  );
  return (
    <button onClick={() => onOpen(r.id)}
      className={`w-full text-left rounded-xl p-3.5 border transition-colors ${isDarkMode
        ? 'bg-white/5 border-white/10 hover:bg-white/[0.08]'
        : 'bg-white border-slate-200 hover:bg-slate-50'}`}>
      <div className="flex items-start justify-between gap-2">
        <span className={`flex items-center gap-1.5 min-w-0 text-sm font-semibold truncate ${isDarkMode ? 'text-white/90' : 'text-slate-800'}`}>
          {r.reported && <ReportedFlag isDarkMode={isDarkMode} t={t} />}
          <span className="truncate">{r.fileName}</span>
        </span>
        <StatusBadge status={r.status} isDarkMode={isDarkMode} t={t} />
      </div>
      <div className={`text-xs truncate mt-0.5 ${textMuted}`}>{r.supplierName || r.supplierId || t('ab.supplierUnknown')}</div>
      <div className="grid grid-cols-2 gap-x-4 gap-y-2 mt-3">
        {meta(t('ab.colOrder'), r.ebeln || '—')}
        {meta(t('ab.colLines'), r.lineCount ?? 0)}
        {meta(t('ab.colCompare'), <CompareChips compare={r.compare} isDarkMode={isDarkMode} t={t} />)}
        {meta(t('ab.colCreated'), fmtDate(r.createdAt))}
      </div>
    </button>
  );
};

const AbDocumentList = ({
  user, isDarkMode, t, onOpenDocument, initialTab = 'open',
  initialStatusFilter = '', initialCommitted = '', showError,
}) => {
  const [tab, setTab] = useState(initialTab);
  const [subStatus, setSubStatus] = useState(initialStatusFilter);
  const [q, setQ] = useState('');
  const [supplierFilter, setSupplierFilter] = useState('');
  const [receivedFrom, setReceivedFrom] = useState('');
  const [receivedTo, setReceivedTo] = useState('');
  const [committedFilter, setCommittedFilter] = useState(initialCommitted);
  const [suppliers, setSuppliers] = useState([]);
  const [page, setPage] = useState(1);
  const [pageItems, setPageItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    listDocumentSuppliers(user).then((res) => setSuppliers(Array.isArray(res) ? res : [])).catch(() => setSuppliers([]));
  }, [user]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const states = subStatus || (tab === 'open' ? OPEN_STATES.join(',') : DONE_STATES.join(','));
      const params = { q, status: states, page, pageSize: PAGE_SIZE };
      // "Ohne Lieferant" = Backend-Kontrakt supplier='' — api.js sendet den Leerstring fuer
      // 'supplier' explizit mit (Sonderfall dort dokumentiert), damit serverseitig gefiltert
      // UND korrekt paginiert wird.
      if (supplierFilter) params.supplier = supplierFilter === NONE_SUPPLIER ? '' : supplierFilter;
      if (receivedFrom) params.receivedFrom = receivedFrom;
      if (receivedTo) params.receivedTo = receivedTo;
      if (committedFilter) params.committed = committedFilter;
      const res = await listDocuments(user, params);
      setPageItems(Array.isArray(res?.items) ? res.items : []);
      setTotal(Number(res?.total) || 0);
    } catch (e) {
      showError?.(`${t('ab.listLoadError')}: ${e.message}`);
      setPageItems([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [user, q, tab, subStatus, supplierFilter, receivedFrom, receivedTo, committedFilter, page, showError, t]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { setPage(1); }, [tab, subStatus, q, supplierFilter, receivedFrom, receivedTo, committedFilter]);

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const filtersActive = !!(supplierFilter || receivedFrom || receivedTo || committedFilter || subStatus);
  const resetFilters = () => { setSubStatus(''); setSupplierFilter(''); setReceivedFrom(''); setReceivedTo(''); setCommittedFilter(''); };

  const subStatusChips = useMemo(() => ([
    { id: '', label: t('ab.shell.filterStatusAll') },
    { id: 'processing', label: t('ab.status.processing') },
    { id: 'no_ruleset', label: t('ab.status.no_ruleset') },
    { id: 'needs_review', label: t('ab.status.needs_review') },
    { id: 'failed', label: t('ab.status.failed') },
  ]), [t]);

  const th = `text-left text-[11px] uppercase tracking-wide font-semibold px-3 py-2 ${isDarkMode ? 'text-white/40' : 'text-slate-400'}`;
  const td = `px-3 py-2.5 text-sm ${isDarkMode ? 'text-white/80' : 'text-slate-700'}`;
  const selectCls = inputCls(isDarkMode);

  return (
    <Card isDarkMode={isDarkMode}>
      {/* Tabs */}
      <div className="flex items-center gap-2 mb-4">
        {[
          { id: 'open', label: t('ab.tabOpen') },
          { id: 'done', label: t('ab.tabDone') },
        ].map(({ id, label }) => (
          <button key={id} onClick={() => { setTab(id); setSubStatus(''); }}
            className={`px-3.5 py-2 rounded-xl text-sm font-medium transition-all ${tab === id
              ? (isDarkMode ? 'bg-indigo-600/70 text-white border border-indigo-400/30' : 'bg-indigo-600 text-white')
              : (isDarkMode ? 'text-white/60 hover:text-white hover:bg-white/10' : 'text-slate-500 hover:text-slate-900 hover:bg-slate-100')
            }`}>
            {label}
          </button>
        ))}
      </div>

      {/* Sub-Status-Chips (nur "Offen") */}
      {tab === 'open' && (
        <div className="flex items-center gap-1.5 mb-3 flex-wrap">
          {subStatusChips.map(({ id, label }) => (
            <button key={id || 'all'} onClick={() => setSubStatus(id)}
              className={`px-2.5 py-1 rounded-lg text-xs font-medium transition-all ${subStatus === id
                ? (isDarkMode ? 'bg-white/15 text-white' : 'bg-slate-800 text-white')
                : (isDarkMode ? 'text-white/50 hover:text-white hover:bg-white/8' : 'text-slate-500 hover:text-slate-800 hover:bg-slate-100')
              }`}>
              {label}
            </button>
          ))}
        </div>
      )}

      {/* Filterzeile 1: Suche + Aktualisieren */}
      <div className="flex flex-wrap items-center gap-3 mb-3">
        <div className="relative w-full sm:w-auto">
          <Search className={`w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 ${isDarkMode ? 'text-white/40' : 'text-slate-400'}`} />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder={t('ab.searchPlaceholder')}
            className={`${inputCls(isDarkMode)} pl-9 w-full sm:w-64`} />
        </div>
        <div className="flex-1" />
        <button onClick={load} className={btnGhost(isDarkMode)} title={t('ab.refresh')}><RefreshCw className="w-4 h-4" /></button>
      </div>

      {/* Filterzeile 2: Lieferant / Zeitraum / Uebertragen (UI-PARITAET) */}
      <div className="flex flex-wrap items-end gap-3 mb-4">
        <label className="flex flex-col gap-1">
          <span className={`text-[11px] uppercase tracking-wide font-semibold ${isDarkMode ? 'text-white/40' : 'text-slate-400'}`}>{t('ab.shell.filterSupplier')}</span>
          <select value={supplierFilter} onChange={(e) => setSupplierFilter(e.target.value)} className={`${selectCls} w-48`}>
            <option value="">{t('ab.shell.filterSupplierAll')}</option>
            {suppliers.filter((s) => s.supplierId).map((s) => (
              <option key={s.supplierId} value={s.supplierId}>{s.supplierName || s.supplierId} ({s.count})</option>
            ))}
            {suppliers.some((s) => !s.supplierId) && (
              <option value={NONE_SUPPLIER}>{t('ab.shell.filterSupplierNone')}</option>
            )}
          </select>
        </label>
        <label className="flex flex-col gap-1">
          <span className={`text-[11px] uppercase tracking-wide font-semibold ${isDarkMode ? 'text-white/40' : 'text-slate-400'}`}>{t('ab.shell.filterFrom')}</span>
          <input type="date" value={receivedFrom} onChange={(e) => setReceivedFrom(e.target.value)} className={`${selectCls} w-36`} />
        </label>
        <label className="flex flex-col gap-1">
          <span className={`text-[11px] uppercase tracking-wide font-semibold ${isDarkMode ? 'text-white/40' : 'text-slate-400'}`}>{t('ab.shell.filterTo')}</span>
          <input type="date" value={receivedTo} onChange={(e) => setReceivedTo(e.target.value)} className={`${selectCls} w-36`} />
        </label>
        <label className="flex flex-col gap-1">
          <span className={`text-[11px] uppercase tracking-wide font-semibold ${isDarkMode ? 'text-white/40' : 'text-slate-400'}`}>{t('ab.shell.filterCommitted')}</span>
          <select value={committedFilter} onChange={(e) => setCommittedFilter(e.target.value)} className={`${selectCls} w-40`}>
            <option value="">{t('ab.shell.filterCommittedAll')}</option>
            <option value="true">{t('ab.shell.filterCommittedYes')}</option>
            <option value="false">{t('ab.shell.filterCommittedNo')}</option>
          </select>
        </label>
        {filtersActive && (
          <button onClick={resetFilters} className={btnGhost(isDarkMode)}>{t('ab.shell.filterReset')}</button>
        )}
      </div>
      {/* Desktop-Tabelle */}
      <div className="hidden md:block overflow-x-auto">
        <table className="w-full border-collapse">
          <thead>
            <tr className={isDarkMode ? 'border-b border-white/10' : 'border-b border-slate-200'}>
              <th className={th}>{t('ab.colFile')}</th>
              <th className={th}>{t('ab.colSupplier')}</th>
              <th className={th}>{t('ab.colOrder')}</th>
              <th className={`${th} text-right`}>{t('ab.colLines')}</th>
              <th className={th}>{t('ab.colCompare')}</th>
              <th className={`${th} text-right`}>{t('ab.colWarnings')}</th>
              <th className={th}>{t('ab.colStatus')}</th>
              <th className={th}>{t('ab.colCreated')}</th>
            </tr>
          </thead>
          <tbody>
            {pageItems.map((r) => (
              <tr key={r.id} onClick={() => onOpenDocument(r.id)}
                className={`cursor-pointer transition-colors ${isDarkMode ? 'hover:bg-white/5 border-b border-white/5' : 'hover:bg-slate-50 border-b border-slate-100'}`}>
                <td className={`${td} font-medium`}>
                  <span className="flex items-center gap-2 min-w-0">
                    <FileScan className={`w-4 h-4 flex-shrink-0 ${isDarkMode ? 'text-white/30' : 'text-slate-300'}`} />
                    {r.reported && <ReportedFlag isDarkMode={isDarkMode} t={t} />}
                    <span className="truncate">{r.fileName}</span>
                  </span>
                </td>
                <td className={td}>{r.supplierName || r.supplierId || t('ab.supplierUnknown')}</td>
                <td className={`${td} font-mono text-xs`}>{r.ebeln || '—'}</td>
                <td className={`${td} text-right tabular-nums`}>{r.lineCount ?? 0}</td>
                <td className={td}><CompareChips compare={r.compare} isDarkMode={isDarkMode} t={t} /></td>
                <td className={`${td} text-right tabular-nums`}>
                  {r.warningCount > 0
                    ? <span className={`inline-flex items-center gap-1 ${isDarkMode ? 'text-amber-300' : 'text-amber-600'}`}><AlertTriangle className="w-3.5 h-3.5" />{r.warningCount}</span>
                    : '—'}
                </td>
                <td className={td}><StatusBadge status={r.status} isDarkMode={isDarkMode} t={t} /></td>
                <td className={td}>{fmtDate(r.createdAt)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Mobil: gestapelte Karten */}
      <div className="md:hidden space-y-2.5">
        {pageItems.map((r) => <MobileDocRow key={r.id} r={r} isDarkMode={isDarkMode} t={t} onOpen={onOpenDocument} />)}
      </div>

      {pageItems.length === 0 && (
        <p className={`text-sm py-8 text-center ${isDarkMode ? 'text-white/40' : 'text-slate-400'}`}>
          {loading ? t('ab.loading') : t('ab.listEmpty')}
        </p>
      )}

      {/* Pagination (serverseitig — gilt fuer ALLE Filter inkl. "Ohne Lieferant") */}
      <Pager page={page} totalPages={totalPages} total={total} onPage={setPage} isDarkMode={isDarkMode} t={t} />
    </Card>
  );
};

export default AbDocumentList;
